import React, { useState, useRef, useEffect } from "react";

const OfferCardAdmin = ({
  offer,
  onDeleteClick,
  onImageUpload,
  onFieldChange,
}) => {
  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) onImageUpload(offer.id, file);
  };

  // position editor state (percentages)
  const initialX = typeof offer.imagePosX === "number" ? offer.imagePosX : 50;
  const initialY = typeof offer.imagePosY === "number" ? offer.imagePosY : 50;
  const initialScale =
    typeof offer.imageScale === "number" ? offer.imageScale : 1;
  // pos in pixels relative to container (left/top of image)
  const [pos, setPos] = useState({ x: initialX, y: initialY });
  const [scale, setScale] = useState(initialScale);
  const containerRef = useRef(null);
  const imgSizeRef = useRef({ w: 0, h: 0 });
  const scaleRef = useRef(scale);
  const posRef = useRef(pos);
  const draggingRef = useRef(false);
  const startRef = useRef({ sx: 0, sy: 0, px: initialX, py: initialY });
  const prevImagePathRef = useRef(offer.imagePath);

  // load natural image size when imagePath changes
  useEffect(() => {
    if (!offer.imagePath) return;
    const img = new Image();
    img.src = offer.imagePath;
    img.onload = () => {
      imgSizeRef.current = { w: img.naturalWidth, h: img.naturalHeight };

      const c = containerRef.current;
      if (c) {
        // Only reset position if it's a new image or no position is set
        const isNewImage = prevImagePathRef.current !== offer.imagePath;
        const hasNoPos =
          typeof offer.imagePosX !== "number" ||
          typeof offer.imagePosY !== "number";

        if (isNewImage || hasNoPos) {
          const cw = c.clientWidth;
          const displayW = cw * scale; // background-size width
          const displayH = (img.naturalHeight / img.naturalWidth) * displayW;
          const px = Math.round((cw - displayW) / 2);
          const py = Math.round((c.clientHeight - displayH) / 2);
          setPos({ x: px, y: py });
          // persist
          onFieldChange(offer.id, "imagePosX", px);
          onFieldChange(offer.id, "imagePosY", py);

          prevImagePathRef.current = offer.imagePath;
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offer.imagePath]);

  useEffect(() => {
    // keep local state in sync if parent updates offer
    setPos({ x: initialX, y: initialY });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offer.imagePosX, offer.imagePosY]);

  // keep refs in sync with state so external listeners can read latest
  useEffect(() => {
    scaleRef.current = scale;
  }, [scale]);
  useEffect(() => {
    posRef.current = pos;
  }, [pos]);

  const handlePointerDown = (e) => {
    // ignore if clicking on resize handle
    // no special-case for resize handle any more (slider used)
    const rect = containerRef.current.getBoundingClientRect();
    draggingRef.current = true;
    startRef.current = {
      sx: e.clientX,
      sy: e.clientY,
      px: pos.x,
      py: pos.y,
      w: rect.width,
      h: rect.height,
    };
    containerRef.current.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e) => {
    if (!draggingRef.current) return;
    const s = startRef.current;
    const dx = e.clientX - s.sx;
    const dy = e.clientY - s.sy;
    // apply 1:1 pixel movement: pos = startPos + delta
    let nx = s.px + dx;
    let ny = s.py + dy;
    // clamp so image always covers viewport (or center if smaller)
    const c = containerRef.current;
    const cw = c.clientWidth;
    const ch = c.clientHeight;
    const displayW = cw * scale;
    const displayH =
      (imgSizeRef.current.h / imgSizeRef.current.w) * displayW || displayW;

    const minX = displayW > cw ? cw - displayW : (cw - displayW) / 2;
    const maxX = displayW > cw ? 0 : (cw - displayW) / 2;
    const minY = displayH > ch ? ch - displayH : (ch - displayH) / 2;
    const maxY = displayH > ch ? 0 : (ch - displayH) / 2;

    nx = Math.max(minX, Math.min(maxX, nx));
    ny = Math.max(minY, Math.min(maxY, ny));
    setPos({ x: nx, y: ny });
  };

  const handlePointerUp = (e) => {
    if (draggingRef.current) {
      draggingRef.current = false;
      try {
        containerRef.current.releasePointerCapture(e.pointerId);
      } catch (err) {}
      // persist pan position
      onFieldChange(offer.id, "imagePosX", pos.x);
      onFieldChange(offer.id, "imagePosY", pos.y);
      return;
    }
  };

  // wheel handler: zoom at cursor position (reads refs for latest values)
  const handleWheel = (originalEvent) => {
    if (!containerRef.current) return;
    const e = originalEvent;
    // prevent default only when listener is non-passive (we attach below)
    if (e.cancelable) e.preventDefault();

    // compute new scale using an exponential factor for smoothness
    const delta = e.deltaY; // positive = scroll down (zoom out)
    const factor = Math.exp(-delta * 0.0015); // tweak sensitivity
    const prevScale = scaleRef.current;
    const newScale = Math.max(0.5, Math.min(3, prevScale * factor));

    const c = containerRef.current;
    const rect = c.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;

    // sizes
    const cw = c.clientWidth;
    const oldDisplayW = cw * prevScale;
    const oldDisplayH =
      (imgSizeRef.current.h / imgSizeRef.current.w) * oldDisplayW ||
      oldDisplayW;

    const posLocal = posRef.current;
    // focal fractions relative to image
    const focalX = (cx - posLocal.x) / oldDisplayW;
    const focalY = (cy - posLocal.y) / oldDisplayH;

    const newDisplayW = cw * newScale;
    const newDisplayH =
      (imgSizeRef.current.h / imgSizeRef.current.w) * newDisplayW ||
      newDisplayW;

    let newPosX = cx - focalX * newDisplayW;
    let newPosY = cy - focalY * newDisplayH;

    // clamp to avoid empty areas
    const minX = newDisplayW > cw ? cw - newDisplayW : (cw - newDisplayW) / 2;
    const maxX = newDisplayW > cw ? 0 : (cw - newDisplayW) / 2;
    const minY =
      newDisplayH > c.clientHeight
        ? c.clientHeight - newDisplayH
        : (c.clientHeight - newDisplayH) / 2;
    const maxY =
      newDisplayH > c.clientHeight ? 0 : (c.clientHeight - newDisplayH) / 2;

    newPosX = Math.max(minX, Math.min(maxX, newPosX));
    newPosY = Math.max(minY, Math.min(maxY, newPosY));

    setScale(newScale);
    setPos({ x: newPosX, y: newPosY });
    onFieldChange(offer.id, "imageScale", newScale);
    onFieldChange(offer.id, "imagePosX", newPosX);
    onFieldChange(offer.id, "imagePosY", newPosY);
  };

  // attach a non-passive wheel listener to prevent page scroll when over preview
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const listener = (e) => handleWheel(e);
    el.addEventListener("wheel", listener, { passive: false });
    return () => el.removeEventListener("wheel", listener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <article className="offer-card card editable">
      <button
        className="delete-btn"
        onClick={() => onDeleteClick(offer.id)}
        title="Elimina offerta"
      >
        ×
      </button>

      <div className="offer-image-container">
        {offer.hasImage && offer.imagePath ? (
          <div className="img-viewport-wrapper">
            <div
              className="img-viewport"
              ref={containerRef}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
              title="Trascina per posizionare l'immagine. Usa la rotella del mouse per zoomare"
              style={{
                backgroundImage: `url(${offer.imagePath})`,
                backgroundPosition: `${Math.round(pos.x)}px ${Math.round(
                  pos.y
                )}px`,
                backgroundSize: `${scale * 100}% auto`,
              }}
            ></div>

            {/* slider removed; zoom handled with mouse wheel */}
          </div>
        ) : (
          <div className="offer-image-placeholder">
            <p>Nessuna immagine</p>
          </div>
        )}

        <label className="image-upload-btn">
          Carica Immagine
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
        </label>
      </div>

      <div className="offer-content">
        <input
          type="text"
          className="editable-title"
          value={offer.title}
          onChange={(e) => onFieldChange(offer.id, "title", e.target.value)}
          placeholder="Titolo offerta"
        />

        <textarea
          className="editable-description"
          value={offer.description}
          onChange={(e) =>
            onFieldChange(offer.id, "description", e.target.value)
          }
          placeholder="Descrizione offerta"
          rows="3"
        />

        <input
          type="text"
          className="editable-price"
          value={offer.prezzo}
          onChange={(e) => onFieldChange(offer.id, "prezzo", e.target.value)}
          placeholder="Prezzo (es. € 350)"
        />
      </div>
    </article>
  );
};

export default OfferCardAdmin;
